import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, BookOpen, CheckCircle, XCircle, Loader2, Award, RotateCcw, Sparkles, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFreemiumAccess } from '@/hooks/useFreemiumAccess';
import { toast } from 'sonner';
import LockedContentModal from '@/components/LockedContentModal';
import PlanSelectionDialog from '@/components/PlanSelectionDialog';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';
import { useTranslatedText, useTranslatedTexts } from '@/hooks/useTranslatedText';

interface ClassData {
  id: string;
  module_id: string;
  class_number: number;
  title: string;
  content: string;
  video_url: string | null;
}

// Helper to extract storage path from a Supabase storage URL
const extractStoragePath = (url: string): string | null => {
  if (!url) return null;
  // Handle both public and signed URLs
  // Format: https://[project].supabase.co/storage/v1/object/public/class-videos/[path]
  const publicMatch = url.match(/\/storage\/v1\/object\/public\/class-videos\/(.+)/);
  if (publicMatch) return publicMatch[1];
  
  // Format: https://[project].supabase.co/storage/v1/object/sign/class-videos/[path]
  const signedMatch = url.match(/\/storage\/v1\/object\/sign\/class-videos\/(.+?)(\?|$)/);
  if (signedMatch) return signedMatch[1];
  
  // If it's just a filename like M1-C1.mp4
  if (!url.includes('/')) return url;
  
  return null;
};

// Exercise interface WITHOUT correct_answer (secure - answers validated server-side)
interface Exercise {
  id: string;
  question_number: number;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
}

// Quiz result from server-side validation
interface QuizResult {
  score: number;
  total: number;
  passed: boolean;
  percentage: number;
  results: Array<{
    exercise_id: string;
    correct: boolean;
    correct_answer?: string; // Only revealed after submission
  }>;
}

interface ClassCompletion {
  id: string;
  score: number;
  total_questions: number;
  passed: boolean;
}

const PASSING_PERCENTAGE = 70;

const Class = () => {
  const { academyId, moduleId, classNumber } = useParams<{ 
    academyId: string; 
    moduleId: string; 
    classNumber: string 
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    hasFullAccess,
    isContentFree,
    canAccessClass: checkCanAccessClass,
    isMonthlyPlan,
    getNextRequiredModule,
    clientType,
    loading: freemiumLoading,
  } = useFreemiumAccess();
  
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [completion, setCompletion] = useState<ClassCompletion | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  // Translated display content (database-driven)
  const { text: translatedClassTitle } = useTranslatedText(
    classData?.title ?? '',
    classData ? `class_title_${classData.id}` : undefined
  );

  const { text: translatedClassContent } = useTranslatedText(
    classData?.content ?? '',
    classData ? `class_content_${classData.id}` : undefined
  );

  const exerciseStrings = useMemo(() => {
    if (exercises.length === 0) return [];
    const out: string[] = [];
    for (const ex of exercises) {
      out.push(ex.question, ex.option_a, ex.option_b, ex.option_c, ex.option_d);
    }
    return out;
  }, [exercises]);

  const { texts: translatedExerciseStrings } = useTranslatedTexts(exerciseStrings);

  const translatedExercises = useMemo(() => {
    if (exercises.length === 0 || translatedExerciseStrings.length === 0) return exercises;

    return exercises.map((ex, idx) => {
      const base = idx * 5;
      return {
        ...ex,
        question: translatedExerciseStrings[base] ?? ex.question,
        option_a: translatedExerciseStrings[base + 1] ?? ex.option_a,
        option_b: translatedExerciseStrings[base + 2] ?? ex.option_b,
        option_c: translatedExerciseStrings[base + 3] ?? ex.option_c,
        option_d: translatedExerciseStrings[base + 4] ?? ex.option_d,
      };
    });
  }, [exercises, translatedExerciseStrings]);

  // Fetch client ID
  useEffect(() => {
    const fetchClientId = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data) {
        setClientId(data.id);
      }
    };
    
    fetchClientId();
  }, [user]);

  // Fetch class data, exercises, and completion status
  useEffect(() => {
    const fetchData = async () => {
      if (!academyId || !moduleId || !classNumber) return;
      
      setLoading(true);
      
      try {
        // First, get the database module ID using academy_id and module_number
        const { data: moduleData, error: moduleError } = await supabase
          .from('modules')
          .select('id')
          .eq('academy_id', academyId)
          .eq('module_number', parseInt(moduleId))
          .maybeSingle();
        
        if (moduleError) throw moduleError;
        if (!moduleData) {
          console.error('Module not found');
          setLoading(false);
          return;
        }
        
        // Fetch class using the database module ID
        const { data: classResult, error: classError } = await supabase
          .from('classes')
          .select('*')
          .eq('module_id', moduleData.id)
          .eq('class_number', parseInt(classNumber))
          .maybeSingle();
        
        if (classError) throw classError;
        if (!classResult) {
          console.error('Class not found');
          setLoading(false);
          return;
        }
        
        setClassData(classResult);
        
        // Generate signed URL for video if it exists
        if (classResult.video_url) {
          const videoPath = extractStoragePath(classResult.video_url);
          if (videoPath) {
            const { data: signedData, error: signedError } = await supabase.storage
              .from('class-videos')
              .createSignedUrl(videoPath, 3600); // 1 hour expiry
            
            if (signedError) {
              console.error('Error generating signed URL:', signedError);
            } else if (signedData?.signedUrl) {
              setSignedVideoUrl(signedData.signedUrl);
            }
          }
        }
        
        // Fetch exercises using secure server-side function (no correct_answer exposed)
        const { data: exercisesResult, error: exercisesError } = await supabase
          .rpc('get_class_exercises', { p_class_id: classResult.id });
        
        if (exercisesError) {
          console.error('Error fetching exercises:', exercisesError);
        } else {
          setExercises(exercisesResult || []);
        }
        
        // Fetch completion status if client ID is available
        if (clientId) {
          const { data: completionResult } = await supabase
            .from('class_completions')
            .select('*')
            .eq('client_id', clientId)
            .eq('class_id', classResult.id)
            .maybeSingle();
          
          setCompletion(completionResult);
        }
      } catch (error) {
        console.error('Error fetching class:', error);
        toast.error('Failed to load class content');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [academyId, moduleId, classNumber, clientId]);

  const handleStartQuiz = () => {
    setShowQuiz(true);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
  };

  const handleSelectAnswer = (answer: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion]: answer
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestion < exercises.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!classData) return;
    
    setSubmittingQuiz(true);
    
    try {
      // Build answers array for server-side validation
      const answersArray = exercises.map((exercise, index) => ({
        exercise_id: exercise.id,
        answer: selectedAnswers[index] || ''
      }));
      
      // Submit to secure server-side function
      const { data, error } = await supabase
        .rpc('submit_quiz_answers', {
          p_class_id: classData.id,
          p_answers: answersArray
        });
      
      if (error) throw error;
      
      // Server returns validated results with correct answers revealed
      const result = data as unknown as QuizResult;
      setQuizResult(result);
      setShowResults(true);
      
      // Update completion state
      setCompletion({
        id: '',
        score: result.score,
        total_questions: result.total,
        passed: result.passed
      });
      
      if (result.passed) {
        toast.success('Congratulations! You passed the exercise!');
      } else {
        toast.error(`You need ${PASSING_PERCENTAGE}% to pass. Try again!`);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleRetakeQuiz = () => {
    setShowQuiz(false);
    setShowResults(false);
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setQuizResult(null);
  };

  // Render markdown-like content
  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let inList = false;
    let listItems: string[] = [];
    
    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 mb-6 text-muted-foreground">
            {listItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Headers
      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-xl font-bold text-foreground mt-8 mb-4">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-lg font-semibold text-foreground mt-6 mb-3">
            {trimmed.replace('### ', '')}
          </h3>
        );
      }
      // Blockquotes
      else if (trimmed.startsWith('> ')) {
        flushList();
        elements.push(
          <blockquote key={index} className="border-l-4 border-primary pl-4 py-2 my-4 bg-primary/5 rounded-r">
            <p className="text-foreground font-medium italic">
              {trimmed.replace('> ', '').replace(/\*\*/g, '')}
            </p>
          </blockquote>
        );
      }
      // List items
      else if (trimmed.startsWith('- ')) {
        inList = true;
        listItems.push(trimmed.replace('- ', ''));
      }
      // Horizontal rule
      else if (trimmed === '---') {
        flushList();
        elements.push(<hr key={index} className="border-border/30 my-8" />);
      }
      // Bold text paragraphs
      else if (trimmed.length > 0 && !trimmed.startsWith('#')) {
        flushList();
        // Handle bold text
        const parts = trimmed.split(/\*\*(.*?)\*\*/g);
        elements.push(
          <p key={index} className="text-muted-foreground leading-relaxed mb-4">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
            )}
          </p>
        );
      }
    });
    
    flushList();
    return elements;
  };

  if (loading || freemiumLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground mb-4">Class not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  // Check if this class is free (Module 1, Class 1 of first academy)
  const isFreeClass = isContentFree(
    academyId || '', 
    parseInt(moduleId || '0'), 
    parseInt(classNumber || '0')
  );
  
  // Use the centralized access check that handles all client types
  const canAccessClass = checkCanAccessClass(
    academyId || '', 
    parseInt(moduleId || '0'), 
    parseInt(classNumber || '0')
  );

  const lockReason = isMonthlyPlan ? 'monthly_incomplete' : 'freemium';
  const previousModuleNumber = isMonthlyPlan
    ? (getNextRequiredModule(academyId || '', parseInt(moduleId || '0')) ?? (parseInt(moduleId || '0') - 1))
    : undefined;

  // If class is locked, show locked modal
  if (!canAccessClass) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 overflow-hidden">
        <LightBackground variant="subtle" />
        <LockedContentModal 
          isOpen={true} 
          onClose={() => navigate(`/academy/${academyId}/module/${moduleId}`)} 
          contentType="class"
          lockReason={lockReason}
          previousModuleNumber={previousModuleNumber}
        />
        
        <header className="relative z-50 px-6 py-4 border-b border-border/20">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate(`/academy/${academyId}/module/${moduleId}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Classes
            </Button>
          </div>
        </header>
      </div>
    );
  }

  const currentExercise = translatedExercises[currentQuestion];
  const percentage = quizResult?.percentage ?? 0;
  const passed = quizResult?.passed ?? false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-50 px-6 py-4 border-b border-border/20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/academy/${academyId}/module/${moduleId}`)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <TranslatedText>Back to Classes</TranslatedText>
          </Button>
          
          <div className="flex items-center gap-2">
            <LanguageSelector variant="ghost" className="text-slate-600 hover:text-slate-900" />
            {completion?.passed && (
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium"><TranslatedText>Completed</TranslatedText></span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {!showQuiz ? (
              // Class Content View
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                {/* Class Header */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    <span className="text-primary text-sm font-medium uppercase tracking-wider">
                      <TranslatedText cacheKey={classData ? `class_label_${classData.id}` : undefined}>
                        {`Class ${classData.class_number}`}
                      </TranslatedText>
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold mt-2 mb-4 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
                    {translatedClassTitle}
                  </h1>
                </div>

                {/* Video Player - uses signed URL for private bucket */}
                {classData.video_url && signedVideoUrl && (
                  <div className="mb-12">
                    <div className="rounded-2xl overflow-hidden glass-panel border border-border/30">
                      <video
                        controls
                        controlsList="nodownload noplaybackrate"
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                        className="w-full aspect-video bg-black"
                        poster=""
                      >
                        <source src={signedVideoUrl} type="video/mp4" />
                        <TranslatedText>Your browser does not support the video tag.</TranslatedText>
                      </video>
                    </div>
                  </div>
                )}

                {/* Class Content */}
                <div className="prose prose-invert max-w-none mb-12 p-8 rounded-2xl glass-panel">
                  {renderContent(translatedClassContent)}
                </div>

                {/* Upgrade CTA for Freemium Users */}
                {!hasFullAccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-10 p-6 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 text-white text-center"
                  >
                    <Sparkles className="w-6 h-6 mx-auto mb-2 text-white/80" />
                    <h3 className="text-lg font-bold mb-1">Enjoyed this class?</h3>
                    <p className="text-sm text-white/80 mb-4">
                      This was just a taste. Unlock all 500+ lessons, exercises, and certificates.
                    </p>
                    <Button
                      onClick={() => setShowPlanDialog(true)}
                      className="bg-white text-blue-600 hover:bg-white/90 font-semibold rounded-xl"
                      size="lg"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Unlock Full Access
                    </Button>
                  </motion.div>
                )}

                {/* Start Exercise Button */}
                {exercises.length > 0 && (
                  <div className="text-center">
                    <div className="mb-6 p-6 rounded-xl glass-panel-strong border-primary/20">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        <TranslatedText>Knowledge Check</TranslatedText>
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        <TranslatedText>
                          {`Complete the exercise with at least ${PASSING_PERCENTAGE}% correct answers to mark this class as done.`}
                        </TranslatedText>
                      </p>
                      {completion && (
                        <p className={`text-sm mb-4 ${completion.passed ? 'text-green-400' : 'text-yellow-400'}`}>
                          <TranslatedText>
                            {`Previous score: ${completion.score}/${completion.total_questions} (${Math.round((completion.score / completion.total_questions) * 100)}%)`}
                          </TranslatedText>
                        </p>
                      )}
                      <Button size="lg" onClick={handleStartQuiz} className="px-8">
                        {completion ? (
                          <TranslatedText>Retake Exercise</TranslatedText>
                        ) : (
                          <TranslatedText>Start Exercise</TranslatedText>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : !showResults ? (
              // Quiz View
              <motion.div
                key="quiz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="max-w-2xl mx-auto"
              >
                {/* Progress */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      <TranslatedText>{`Question ${currentQuestion + 1} of ${exercises.length}`}</TranslatedText>
                    </span>
                    <span className="text-sm text-muted-foreground">
                      <TranslatedText>{`${Object.keys(selectedAnswers).length} answered`}</TranslatedText>
                    </span>
                  </div>
                  <div className="h-2 bg-muted/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${((currentQuestion + 1) / exercises.length) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Question */}
                <div className="p-8 rounded-2xl glass-panel mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-6">
                    {currentExercise?.question}
                  </h2>
                  
                  <div className="space-y-3">
                    {['A', 'B', 'C', 'D'].map((option) => {
                      const optionKey = `option_${option.toLowerCase()}` as keyof Exercise;
                      const isSelected = selectedAnswers[currentQuestion] === option;
                      
                      return (
                        <button
                          key={option}
                          onClick={() => handleSelectAnswer(option)}
                          className={`w-full p-4 rounded-xl text-left transition-all duration-200 border ${
                            isSelected 
                              ? 'bg-primary/20 border-primary text-foreground' 
                              : 'bg-muted/10 border-border/30 text-muted-foreground hover:border-primary/50 hover:bg-muted/20'
                          }`}
                        >
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted/20 text-sm font-medium mr-3">
                            {option}
                          </span>
                          {currentExercise?.[optionKey] as string}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={handlePrevQuestion}
                    disabled={currentQuestion === 0}
                  >
                    <TranslatedText>Previous</TranslatedText>
                  </Button>
                  
                  {currentQuestion === exercises.length - 1 ? (
                    <Button
                      onClick={handleSubmitQuiz}
                      disabled={Object.keys(selectedAnswers).length < exercises.length || submittingQuiz}
                    >
                      {submittingQuiz ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          <TranslatedText>Submitting...</TranslatedText>
                        </>
                      ) : (
                        <TranslatedText>Submit Answers</TranslatedText>
                      )}
                    </Button>
                  ) : (
                    <Button onClick={handleNextQuestion}>
                      <TranslatedText>Next</TranslatedText>
                    </Button>
                  )}
                </div>

                {/* Question dots */}
                <div className="flex items-center justify-center gap-2 mt-8">
                  {exercises.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentQuestion 
                          ? 'bg-primary scale-125' 
                          : selectedAnswers[index] 
                            ? 'bg-primary/50' 
                            : 'bg-muted/30'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>
            ) : (
              // Results View
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="max-w-2xl mx-auto text-center"
              >
                <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
                  passed ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {passed ? (
                    <Award className="w-12 h-12 text-green-400" />
                  ) : (
                    <XCircle className="w-12 h-12 text-red-400" />
                  )}
                </div>
                
                <h2 className="text-3xl font-bold mb-2 text-foreground">
                  {passed ? (
                    <TranslatedText>Congratulations!</TranslatedText>
                  ) : (
                    <TranslatedText>Not Quite!</TranslatedText>
                  )}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {passed ? (
                    <TranslatedText>You have successfully completed this class exercise.</TranslatedText>
                  ) : (
                    <TranslatedText>
                      {`You need at least ${PASSING_PERCENTAGE}% to pass. Keep studying and try again!`}
                    </TranslatedText>
                  )}
                </p>
                
                <div className="p-8 rounded-2xl glass-panel mb-8">
                  <div className="text-6xl font-bold mb-2" style={{ color: passed ? '#4ade80' : '#f87171' }}>
                    {percentage}%
                  </div>
                  <p className="text-muted-foreground">
                    <TranslatedText>
                      {`${quizResult?.score ?? 0} out of ${quizResult?.total ?? exercises.length} correct`}
                    </TranslatedText>
                  </p>
                </div>

                {/* Answer Review - uses server-side results for correct answers */}
                <div className="text-left mb-8">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">
                    <TranslatedText>Review Answers</TranslatedText>
                  </h3>
                  <div className="space-y-3">
                    {quizResult?.results.map((result, index) => {
                      const exercise = translatedExercises.find(e => e.id === result.exercise_id);
                      if (!exercise) return null;
                      
                      return (
                        <div 
                          key={result.exercise_id}
                          className={`p-4 rounded-xl border ${
                            result.correct 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-red-500/10 border-red-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {result.correct ? (
                              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                            )}
                            <div>
                              <p className="text-sm text-foreground font-medium">
                                {exercise.question_number}. {exercise.question}
                              </p>
                              {!result.correct && result.correct_answer && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  <TranslatedText>Correct answer:</TranslatedText> {result.correct_answer}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  {passed ? (
                    <Button onClick={() => navigate(`/academy/${academyId}/module/${moduleId}`)}>
                      <TranslatedText>Back to Classes</TranslatedText>
                    </Button>
                  ) : (
                    <Button onClick={handleRetakeQuiz}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      <TranslatedText>Review Class Material</TranslatedText>
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <PlanSelectionDialog open={showPlanDialog} onOpenChange={setShowPlanDialog} />
    </div>
  );
};

export default Class;